import { useMemo } from 'react';
import { StateDetail } from '@weave/schema-gen-ts/dist/schemas/platform-location-feature/v1/location_features.pb';
import { Feature } from '@weave/schema-gen-ts/dist/shared/feature/location_feature.pb';
import dayjs from 'dayjs';
import { useGetBatchFeatureStateHistoryQuery } from '../queries/use-get-batch-feature-state-history';

export type FeatureTrialDateRange = {
  startDate?: string;
  endDate?: string;
};

type UseFeatureTrialExpiredLocationsParams = {
  locationIds: string[];
  featureEnum: Feature;
};

type UseFeatureTrialExpiredLocations = {
  trialExpiredUnsubscribedLocationIds: string[];
  trialExpiredUnsubscribedDateRangeMap: Record<string, FeatureTrialDateRange>;
  isLoading: boolean;
};

/**
 * Determines whether a location has an expired trial without a trustworthy active subscription after it.
 *
 * Logic:
 * - If no "trial" state exists, or the trial has not ended yet -> false
 * - If no "active" state exists -> true
 * - If "active" exists with a startDate and no endDate -> false (currently active)
 * - If "active" exists with no endDate and no startDate -> true (malformed/incomplete active state)
 * - Otherwise compare the trial and active end dates to determine which state ended last
 */
export const isTrialExpiredWithoutResubscription = (states: Record<string, StateDetail> | undefined): boolean => {
  if (!states) {
    return false;
  }

  const trialState = states['trial'];
  const activeState = states['active'];

  // If trial date is not present or trial end date is in the future, then trial is not expired yet
  if (!trialState || !trialState.endDate || dayjs(trialState.endDate).isAfter(dayjs())) {
    return false;
  }

  // No active state ever existed — trial expired without subscribing
  if (!activeState) {
    return true;
  }

  if (!activeState.endDate) {
    // If startDate is empty and endDate is empty, then the active state is malformed and we should consider the trial expired without subscribing
    return !activeState.startDate;
  }

  // If not active after trial, then trial expired without subscribing
  return dayjs(trialState.endDate).isAfter(dayjs(activeState.endDate));
};

/**
 * A hook that determines which of the provided locationIds have an expired trial
 * without a subsequent re-subscription to the specified feature.
 *
 * @param locationIds - The location IDs to check for trial expiry state.
 * @param featureEnum - The feature to check trial expiry for (e.g., CALL_INTELLIGENCE, MESSAGING).
 */
export const useFeatureTrialExpiredLocations = ({
  locationIds,
  featureEnum,
}: UseFeatureTrialExpiredLocationsParams): UseFeatureTrialExpiredLocations => {
  const { data, isLoading } = useGetBatchFeatureStateHistoryQuery({
    request: {
      locationIds,
      featureEnum,
    },
    options: {
      enabled: locationIds.length > 0,
    },
  });

  const { trialExpiredUnsubscribedLocationIds, trialExpiredUnsubscribedDateRangeMap } = useMemo(() => {
    if (!data?.locations) {
      return {
        trialExpiredUnsubscribedLocationIds: [],
        trialExpiredUnsubscribedDateRangeMap: {},
      };
    }

    const dateRangeMap: Record<string, FeatureTrialDateRange> = {};

    Object.entries(data.locations).forEach(([locationId, locationHistory]) => {
      const states = locationHistory.states;

      if (!isTrialExpiredWithoutResubscription(states)) {
        return;
      }

      const trialState = states?.['trial'];

      dateRangeMap[locationId] = {
        startDate: trialState?.startDate,
        endDate: trialState?.endDate,
      };
    });

    const expiredUnsubscribedLocationIds = Object.keys(dateRangeMap);

    return {
      trialExpiredUnsubscribedLocationIds: expiredUnsubscribedLocationIds,
      trialExpiredUnsubscribedDateRangeMap: dateRangeMap,
    };
  }, [data]);

  return {
    trialExpiredUnsubscribedLocationIds,
    trialExpiredUnsubscribedDateRangeMap,
    isLoading,
  };
};
